import React, { useState, useEffect } from 'react'
import Icon from 'components/Icon'
import { Transition } from '@headlessui/react'
import { Link } from 'react-router-dom'
import useApi from 'hooks/useApi'
import messages from "./messages";
import {FormattedMessage, useIntl} from "react-intl";
import { Button, Input, PhoneInput } from '@arnsbogroup/react-voicearchive'

const InviteUserModal = ({ show = false, onInvite }) => {
  const [contacts, loading, errors, refetch] = useApi(`/rest/v2/customer/contacts`, { load: false })
  const [state, setState] = useState({})
  const [users, setUsers] = useState([
    { name: '', email: '', phone: { number: '', country: 'dk' } },
    { name: '', email: '', phone: { number: '', country: 'dk' } },
  ])
  const { formatMessage } = useIntl()

  const onChange = (changes, i) => {
    setUsers(Object.assign([], users, { [i]: {
      ...users[i],
      ...changes,
    }}))
  }

  const onSubmit = (event) => {
    event.preventDefault()

    refetch({
      method: 'POST',
      body: JSON.stringify(
        users
          .filter(({ email }) => email !== '')
          .map((user) => ({ ...user, phone: user.phone.number }))
      )
    })
  }

  return (
    <Transition show={show} className="fixed z-10 inset-0 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <Transition.Child
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          className="fixed inset-0 transition-opacity"
          ariaHidden="true"
        >
          <div className="absolute inset-0 bg-gray-200 opacity-75"></div>
        </Transition.Child>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <Transition.Child
          enter="ease-out duration-200"
          enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enterTo="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 translate-y-0 sm:scale-100"
          leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          className="inline-block align-bottom bg-white rounded-sm px-4 pt-5 pb-4 text-left overflow-hidden shadow transform transition-all sm:my-8 sm:align-middle w-lg  sm:p-6"
          role="dialog"
          ariaModal="true"
          ariaLabeledBy="modal-headline"
        >
          { errors.map((error) => (
            <span className="rounded-sm bg-red-50 p-3 block mb-3">
              <div className="flex">
                <div className="shrink-0">
                  <Icon icon="times-circle" className="text-red-800" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    { error }
                  </h3>
                </div>
              </div>
            </span>
          )) }

          { !!contacts ? (
            <React.Fragment>
              <div>
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                  <Icon icon="check" className="text-green-600" />
                </div>
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-headline">
                    <FormattedMessage {...messages.success}/>
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      <FormattedMessage {...messages.success}/>
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-5 sm:mt-6">
                <Link to="/company/team">
                  <Button className="w-full inline-flex">
                    <FormattedMessage {...messages.close}/>
                  </Button>
                </Link>
              </div>
            </React.Fragment>
          ) : (
            <React.Fragment>
              <form className="space-y-2" onSubmit={onSubmit}>
                {users.map(({ name, email, phone }, i) => (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" key={`user-${i}`}>
                    <div>
                      <label htmlFor={`name-${i}`} className="sr-only">
                        <FormattedMessage {...messages.name} />
                      </label>
                      <Input
                        id={`name-${i}`}
                        name="name"
                        value={name}
                        placeholder={formatMessage(messages.namePlaceholder)}
                        onChange={({ target: { value } }) => onChange({ name: value }, i)}
                      />
                    </div>

                    <div>
                      <label htmlFor={`email-${i}`} className="sr-only">
                        <FormattedMessage {...messages.email} />
                      </label>
                      <Input
                        id={`email-${i}`}
                        type="email"
                        name="email"
                        placeholder={formatMessage(messages.emailPlaceholder)}
                        value={email}
                        onChange={({ target: { value } }) => onChange({ email: value }, i)}
                      />
                    </div>

                    <div>
                      <label htmlFor={`phone-${i}`} className="sr-only">
                        <FormattedMessage {...messages.phone} />
                      </label>
                      <PhoneInput
                        id={`phone-${i}`}
                        country={phone.country}
                        enableSearch
                        disableSearchIcon
                        searchPlaceholder="Search"
                        searchNotFound="Not found"
                        value={phone.number}
                        onChange={(phone, country) => {
                          const data = {
                            number: '+' + phone,
                            country: country.countryCode,
                          }
                          onChange({ phone: data }, i)
                        }}
                      />
                    </div>
                  </div>
                ))}

                <Button
                  variant="inline"
                  size='xs'
                  onClick={() => setUsers([...users, { name: '', email: '', phone: { number: '', country: 'dk' } }])}
                  title={formatMessage(messages.add)}
                  icon={['fa', 'plus']}
                >
                  <FormattedMessage {...messages.add}/>
                </Button>

                <Button 
                  size='sm'
                  type="submit" 
                  disabled={loading} 
                  className="w-full">
                  <FormattedMessage {...messages.invite}/> <Icon icon="spinner-third" className="animate-spin ml-2" visible={loading} />
                </Button>
              </form>
            </React.Fragment>
          ) }

        </Transition.Child>
      </div>
    </Transition>
  )
}

export default InviteUserModal

